import { useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ConfirmModal from "../../components/ConfirmModal";

const inputClass =
  "border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

/**
 * Personeelsleden en de koppeling met hun login. Een gewone gebruiker ziet enkel zijn eigen
 * prestaties via deze koppeling, dus een personeelslid zonder koppeling kan nog niet inloggen
 * als zichzelf.
 */
export default function StaffPanel({ staff, users, onChanged, onError }) {
  const [newName, setNewName] = useState("");
  const [linking, setLinking] = useState({}); // member_id -> gekozen user_id
  const [busy, setBusy] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      await onChanged();
    } catch (err) {
      onError(err.message || "Er ging iets mis.");
    } finally {
      setBusy(false);
    }
  };

  const addStaff = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    run(async () => {
      await apiFetch("/timesheets/staff", { method: "POST", body: JSON.stringify({ naam: newName.trim() }) });
      setNewName("");
    });
  };

  const link = (memberId) => {
    const userId = linking[memberId];
    if (!userId) return;
    run(async () => {
      await apiFetch(`/timesheets/staff/${memberId}/user`, { method: "PUT", body: JSON.stringify({ user_id: userId }) });
      setLinking((cur) => ({ ...cur, [memberId]: "" }));
    });
  };

  const unlink = (memberId) =>
    run(async () => {
      await apiFetch(`/timesheets/staff/${memberId}/user`, { method: "DELETE" });
      setConfirmUnlink(null);
    });

  const availableUsers = users.filter((u) => u.is_active !== false && !u.linked_member_id);

  return (
    <div>
      {confirmUnlink && (
        <ConfirmModal
          title="Login ontkoppelen"
          message={`${confirmUnlink.naam} kan daarna niet meer als zichzelf prestaties ingeven tot er opnieuw een login gekoppeld wordt.`}
          confirmLabel="Ontkoppelen"
          danger
          busy={busy}
          onConfirm={() => unlink(confirmUnlink.id)}
          onCancel={() => setConfirmUnlink(null)}
        />
      )}

      <form onSubmit={addStaff} className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Nieuw personeelslid</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Naam (bv. Janssens, Kelly)"
            className={`${inputClass} w-64`}
          />
        </div>
        <button
          type="submit"
          disabled={busy || !newName.trim()}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
        >
          Toevoegen
        </button>
      </form>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
              <th className="px-4 py-2 text-left">Personeelslid</th>
              <th className="px-4 py-2 text-left">Gekoppelde login</th>
              <th className="px-4 py-2 text-right">Lijnen</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">Nog geen personeelsleden.</td>
              </tr>
            )}
            {staff.map((s) => (
              <tr key={s.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-800">
                  {s.naam}
                  {!s.is_active && <span className="ml-2 text-xs text-gray-400">(inactief)</span>}
                </td>
                <td className="px-4 py-2">
                  {s.user_id ? (
                    <span className="text-gray-700">{s.user_email || s.user_id}</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <select
                        value={linking[s.id] || ""}
                        onChange={(e) => setLinking((cur) => ({ ...cur, [s.id]: e.target.value }))}
                        className={`${inputClass} w-64`}
                      >
                        <option value="">Niet gekoppeld — kies een login...</option>
                        {availableUsers.map((u) => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.email}{u.first_name ? ` (${u.first_name} ${u.last_name || ""})` : ""}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => link(s.id)}
                        disabled={busy || !linking[s.id]}
                        className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Koppelen
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-gray-600">{s.line_count}</td>
                <td className="px-4 py-2 text-right">
                  {s.user_id && (
                    <button onClick={() => setConfirmUnlink(s)} className="text-xs text-red-500 hover:text-red-700">
                      Ontkoppelen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Een login verschijnt hier pas nadat de persoon een uitnodiging heeft aanvaard en een profiel heeft.
      </p>
    </div>
  );
}
